import { FinanceFinancialDocumentFilter } from 'generated/resources/graphql'
import moment from 'moment'

import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { buildFinancialDocumentsAmountWidget } from 'modules/finance/components/Widgets/FinancialDocumentsAmountWidget'
import { buildFinancialDocumentsCountWidget } from 'modules/finance/components/Widgets/FinancialDocumentsCountWidget'
import { DataTableWidgetInsights } from 'modules/finance/components/Widgets/common/widgets'

import { buildFinancialDocumentsTaxesAmountWidget } from '../Widgets/FinancialDocumentsTaxesAmountWidget'

interface FinancialDocumentInsightsProps {
  companyId: number
  currency: string
  locale: string
  canSendToPay: boolean
}

export const FinancialDocumentInsights = ({
  companyId,
  currency,
  locale,
  canSendToPay,
}: FinancialDocumentInsightsProps) => {
  const { getFiltersVariables } = useFilters()

  const amountFormatter = (value: unknown, amountCurrency?: string) =>
    formattedAmount({ amountCents: Number(value), currency: amountCurrency || currency, locale })
  const dataTableFilters: FinanceFinancialDocumentFilter = getFiltersVariables().filter ?? {}

  const totalValueWidget = buildFinancialDocumentsAmountWidget({
    widgetTitle: i18n.t('finance.overview.financial_documents.total_value'),
    widgetId: 'financialDocumentsAmount',
    filters: dataTableFilters,
    prepareDataArgs: { amountFormatter },
    queryArgs: {
      companyId,
    },
  })

  const sentToPayWidget = buildFinancialDocumentsCountWidget({
    widgetTitle: i18n.t('finance.invoices.list.tabs.sent_to_pay_no_counter'),
    filters: dataTableFilters,
    widgetId: 'financialDocumentsSentToPayCount',
    queryArgs: {
      companyId,
      statuses: ['sent_to_pay'],
    },
  })

  const pendingCountWidget = buildFinancialDocumentsCountWidget({
    widgetTitle: i18n.t('finance.overview.financial_documents.outstanding'),
    filters: dataTableFilters,
    widgetId: 'financialDocumentsOutstandingCount',
    queryArgs: {
      companyId,
      statuses: ['review'],
    },
  })

  const overdueCountWidget = buildFinancialDocumentsCountWidget({
    widgetTitle: i18n.t('finance.overview.financial_documents.overdue'),
    filters: dataTableFilters,
    widgetId: 'financialDocumentsOverdueCount',
    queryArgs: {
      companyId,
      statuses: ['review', 'processing', 'sent_to_pay'],
      toDueDate: moment().subtract(1, 'days').utc().startOf('day').toDate().toISOString(),
    },
  })
  const taxesValueWidget = buildFinancialDocumentsTaxesAmountWidget({
    widgetTitle: i18n.t('finance.overview.financial_documents.taxes_value'),
    widgetId: 'financialDocumentsTaxesAmount',
    filters: dataTableFilters,
    prepareDataArgs: { amountFormatter },
    queryArgs: {
      companyId,
    },
  })

  let widgets
  if (canSendToPay) {
    widgets = [
      totalValueWidget,
      sentToPayWidget,
      pendingCountWidget,
      overdueCountWidget,
      taxesValueWidget,
    ]
  } else {
    widgets = [totalValueWidget, pendingCountWidget, overdueCountWidget, taxesValueWidget]
  }
  return <DataTableWidgetInsights useBox widgets={widgets} widgetWidth="full" />
}
